import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Check, Plus, Minus, Calendar, Sparkles, ChevronRight, AlertCircle, X, Wand2, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { mealPlanAPI, profileAPI, smartFillAPI } from '../utils/api';
import { toast } from 'sonner';
import { trackPlanGenerateStart, trackPlanGenerateSuccess, trackPlanGenerateFail, trackRegenerateWeek } from '@/lib/posthog';
import { formatDateString, getTodayString, normalizeDate } from '../utils/dateUtils';
import { distributeWeeklyPlan } from '../utils/weeklyPlanDistribute';
import { hasRealNutritionTargets } from '../utils/nutritionTargets';
import NutritionTargetsSetupPrompt from '../components/NutritionTargetsSetupPrompt';

// Weekly Meal Planner - Select meals for the week and generate optimized plan
export default function WeeklyMealPlanner() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Planning mode: 'smart' (auto-generate) or 'browse' (manual selection)
  const [planningMode, setPlanningMode] = useState('smart');
  
  // Get the start date for the week (default to next Monday)
  const getDefaultStartDate = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilMonday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek);
    const monday = new Date(today);
    monday.setDate(today.getDate() + daysUntilMonday);
    return formatDateString(monday);
  };
  
  const [startDate, setStartDate] = useState(searchParams.get('start') || getDefaultStartDate());
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  // Smart plan preferences
  const [quickPrep, setQuickPrep] = useState(false);
  const [fewerIngredients, setFewerIngredients] = useState(false);
  
  // Meals organized by category
  const [mealsByCategory, setMealsByCategory] = useState({
    breakfast: [],
    lunch: [],
    dinner: [],
    snack: []
  });
  
  // Selected meals by category (up to 7 each)
  const [selectedMeals, setSelectedMeals] = useState({
    breakfast: [],
    lunch: [],
    dinner: [],
    snack: []
  });
  
  // User profile for macro targets
  const [profile, setProfile] = useState(null);
  
  // Existing plans for the week
  const [existingPlans, setExistingPlans] = useState([]);
  
  // UI state
  const [activeCategory, setActiveCategory] = useState('breakfast');
  const [showExistingPlansModal, setShowExistingPlansModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinnedMeals, setPinnedMeals] = useState({}); // { mealId: 'YYYY-MM-DD' }
  const [generatedPlan, setGeneratedPlan] = useState(null);
  
  // Category config
  const CATEGORIES = [
    { id: 'breakfast', label: 'Breakfast', emoji: '🍳' },
    { id: 'lunch', label: 'Lunch', emoji: '🥗' },
    { id: 'dinner', label: 'Dinner', emoji: '🍽️' },
    { id: 'snack', label: 'Snacks', emoji: '🍎', optional: true }
  ];
  
  // Get week dates
  const getWeekDates = useCallback(() => {
    const dates = [];
    // normalizeDate parses "YYYY-MM-DD" as local midday — raw `new Date(str)`
    // parses it as UTC midnight, which shifts the whole week back one day in
    // any negative-UTC-offset timezone (see AGENTS.md pre-launch audit,
    // finding DATE-1).
    const start = normalizeDate(startDate);
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      dates.push(formatDateString(d));
    }
    return dates;
  }, [startDate]);
  
  // Load meals and profile
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Load profile for macro targets
        const profileRes = await profileAPI.getProfile();
        setProfile(profileRes.data);
        
        // Load all available meals
        const mealsRes = await mealPlanAPI.browseMealsWithMacros({
          date: startDate,
          page: 1,
          limit: 500
        });
        
        const meals = mealsRes.data.meals || mealsRes.data || [];
        
        // Organize meals by category/meal_type
        const organized = {
          breakfast: [],
          lunch: [],
          dinner: [],
          snack: []
        };
        
        meals.forEach(meal => {
          const category = (meal.category || meal.meal_type || 'lunch').toLowerCase();
          if (organized[category]) {
            organized[category].push(meal);
          } else {
            organized.lunch.push(meal); // Default to lunch
          }
        });
        
        setMealsByCategory(organized);
        
        // Check for existing plans in this week
        const weekDates = getWeekDates();
        const existingRes = await Promise.all(
          weekDates.map(date => mealPlanAPI.getDailyPlan(date).catch(() => ({ data: [] })))
        );
        
        const existing = existingRes.flatMap((r, idx) => 
          (r.data || []).map(p => ({ ...p, date: weekDates[idx] }))
        );
        
        setExistingPlans(existing);
        
        // If there are existing plans, show the modal
        if (existing.length > 0) {
          setShowExistingPlansModal(true);
        }
        
      } catch (err) {
        console.error('Failed to load data:', err);
        toast.error('Failed to load meals');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [startDate, getWeekDates]);
  
  // Calculate total selected macros for the week
  const getWeeklyMacros = useCallback(() => {
    let totalCal = 0, totalProtein = 0, totalCarbs = 0, totalFat = 0;
    
    Object.values(selectedMeals).forEach(meals => {
      meals.forEach(meal => {
        totalCal += meal.calories || 0;
        totalProtein += meal.protein || 0;
        totalCarbs += meal.carbs || 0;
        totalFat += meal.fat || 0;
      });
    });
    
    return { calories: totalCal, protein: totalProtein, carbs: totalCarbs, fat: totalFat };
  }, [selectedMeals]);
  
  // Get weekly targets based on profile. Returns null when the user has no
  // real, personalized targets yet — callers must not substitute a generic
  // fallback and present it as the user's own (see AGENTS.md pre-launch
  // audit, finding ONB-2).
  const getWeeklyTargets = useCallback(() => {
    if (!hasRealNutritionTargets(profile)) return null;
    const daily = {
      calories: profile.target_calories ?? profile.calories,
      protein: profile.target_protein_g ?? profile.protein,
      carbs: profile.target_carbs_g ?? profile.carbs,
      fat: profile.target_fat_g ?? profile.fat
    };
    return {
      calories: daily.calories * 7,
      protein: daily.protein * 7,
      carbs: daily.carbs * 7,
      fat: daily.fat * 7
    };
  }, [profile]);
  
  // Toggle meal selection
  const toggleMealSelection = (meal, category) => {
    setSelectedMeals(prev => {
      const current = prev[category] || [];
      const isSelected = current.some(m => m.meal_id === meal.meal_id);
      
      if (isSelected) {
        // Remove meal
        return {
          ...prev,
          [category]: current.filter(m => m.meal_id !== meal.meal_id)
        };
      } else {
        // Add meal (max 7)
        if (current.length >= 7) {
          toast.error(`Maximum 7 ${category} meals allowed`);
          return prev;
        }
        return {
          ...prev,
          [category]: [...current, meal]
        };
      }
    });
  };
  
  // Check if a meal is selected
  const isMealSelected = (mealId, category) => {
    return (selectedMeals[category] || []).some(m => m.meal_id === mealId);
  };
  
  // Get total selected count
  const getTotalSelectedCount = () => {
    return Object.values(selectedMeals).reduce((sum, meals) => sum + meals.length, 0);
  };
  
  // Handle clear existing plans
  const handleClearExisting = async () => {
    try {
      // Delete all existing plans for the week
      for (const plan of existingPlans) {
        await mealPlanAPI.deleteMealPlan(plan.plan_id);
      }
      setExistingPlans([]);
      setShowExistingPlansModal(false);
      toast.success('Existing plans cleared');
    } catch (err) {
      console.error('Failed to clear plans:', err);
      toast.error('Failed to clear existing plans');
    }
  };
  
  // Handle keep existing and build around
  const handleKeepExisting = () => {
    setShowExistingPlansModal(false);
    toast.info('Building around existing meals');
  };
  
  // Smart Plan: Auto-generate the entire week
  const handleSmartPlanGenerate = async () => {
    const weekDates = getWeekDates();
    
    setGenerating(true);
    trackPlanGenerateStart(weekDates.length);
    trackRegenerateWeek();
    
    try {
      const preferences = {
        fewer_ingredients: fewerIngredients,
        quick_prep: quickPrep,
        exclude_types: []
      };
      
      // If we have existing plans to keep, pass their plan_ids
      const mealsToKeep = existingPlans.length > 0 ? existingPlans.map(p => p.plan_id) : null;
      
      console.log('[SmartPlan] Generating with mealsToKeep:', mealsToKeep?.length || 0);
      
      const res = await smartFillAPI.auto(weekDates, preferences, [], null, null, mealsToKeep);
      
      if (res.data.meals_added > 0) {
        trackPlanGenerateSuccess(weekDates.length, res.data.meals_added);
        toast.success(`${res.data.meals_added} meals added to your week!`);
        
        // Navigate to weekly review or home
        if (res.data.added_plans && res.data.added_plans.length > 0) {
          navigate('/plan/weekly-review?start=' + startDate);
        }
      } else {
        toast.info('All selected days are already planned');
      }
    } catch (err) {
      console.error('[SmartPlan] Error:', err);
      trackPlanGenerateFail(err.response?.data?.detail || 'api_error');
      toast.error('Failed to generate smart plan');
    } finally {
      setGenerating(false);
    }
  };

  // Generate the optimized meal plan
  const handleGeneratePlan = async () => {
    const totalSelected = getTotalSelectedCount();
    if (totalSelected < 3) {
      toast.error('Please select at least 3 meals');
      return;
    }
    
    // First check if any meals need to be pinned to specific dates
    setShowPinModal(true);
  };
  
  // Execute plan generation after pinning
  const executeGeneration = async () => {
    setShowPinModal(false);
    setGenerating(true);
    
    try {
      const weekDates = getWeekDates();
      const dailyTargets = {
        calories: profile?.daily_calories || 2000,
        protein: profile?.daily_protein || 150,
        carbs: profile?.daily_carbs || 200,
        fat: profile?.daily_fat || 70
      };
      
      // Algorithm: Distribute meals across days to balance daily macros.
      // ARRAY-SAFE (Step 3): each category holds a LIST so a day can legitimately
      // carry two Lunches / two Dinners / multiple Snacks. No row is ever dropped
      // and each real row is persisted independently; native meal_type preserved.
      const plan = distributeWeeklyPlan(weekDates, selectedMeals, pinnedMeals, existingPlans);

      // Create the meal plan entries (only NEW meals; existing rows untouched).
      // Every row in every category list is created independently.
      for (const [date, dayMeals] of Object.entries(plan)) {
        for (const [mealType, meals] of Object.entries(dayMeals)) {
          for (const meal of meals) {
            if (meal && !meal.existing) {
              await mealPlanAPI.createMealPlan({
                date,
                meal_type: mealType.charAt(0).toUpperCase() + mealType.slice(1),
                meal_id: meal.meal_id,
                meal_name: meal.name || meal.title,
                macros: {
                  calories: meal.calories,
                  protein: meal.protein,
                  carbs: meal.carbs,
                  fat: meal.fat
                }
              });
            }
          }
        }
      }
      
      setGeneratedPlan(plan);
      toast.success('Meal plan generated!');
      
      // Navigate to the review page
      navigate(`/plan/weekly-review?start=${startDate}`);
      
    } catch (err) {
      console.error('Failed to generate plan:', err);
      toast.error('Failed to generate meal plan');
    } finally {
      setGenerating(false);
    }
  };
  
  // Calculate selection counts per category
  const getCategoryCount = (category) => {
    return (selectedMeals[category] || []).length;
  };
  
  const weeklyMacros = getWeeklyMacros();
  const weeklyTargets = getWeeklyTargets();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/50 px-4 py-3 header-safe-top">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2">
            <ArrowLeft size={24} />
          </button>
          <h1 className="font-semibold">Plan Your Week</h1>
          <div className="w-10" />
        </div>
        
        {/* Week indicator */}
        <div className="mt-2 text-center text-sm text-muted-foreground">
          Week of {normalizeDate(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </div>
      </header>
      
      {/* Mode Toggle */}
      <div className="px-4 py-3 bg-card border-b border-border/50">
        <div className="flex gap-2">
          <button
            onClick={() => setPlanningMode('smart')}
            data-testid="mode-smart-btn"
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all ${
              planningMode === 'smart'
                ? 'bg-accent text-white shadow-lg'
                : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
            }`}
          >
            <Wand2 size={18} />
            Smart Plan
          </button>
          <button
            onClick={() => setPlanningMode('browse')}
            data-testid="mode-browse-btn"
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all ${
              planningMode === 'browse'
                ? 'bg-accent text-white shadow-lg'
                : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
            }`}
          >
            <Search size={18} />
            Browse & Select
          </button>
        </div>
      </div>
      
      {/* Smart Plan Mode */}
      {planningMode === 'smart' && (
        <div className="px-4 py-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
              <Wand2 size={32} className="text-accent" />
            </div>
            <h2 className="text-xl font-bold mb-2">Auto-Generate Your Week</h2>
            <p className="text-muted-foreground text-sm">
              We'll create a balanced meal plan that hits your macro targets for the entire week.
            </p>
          </div>
          
          {/* Preferences */}
          <div className="space-y-3 mb-6">
            <label className="flex items-center justify-between p-4 bg-card rounded-xl border border-border/50">
              <div>
                <div className="font-medium">Quick Prep</div>
                <div className="text-xs text-muted-foreground">Meals under 20 minutes</div>
              </div>
              <input
                type="checkbox"
                checked={quickPrep}
                onChange={(e) => setQuickPrep(e.target.checked)}
                className="w-5 h-5 rounded accent-accent"
              />
            </label>
            
            <label className="flex items-center justify-between p-4 bg-card rounded-xl border border-border/50">
              <div>
                <div className="font-medium">Fewer Ingredients</div>
                <div className="text-xs text-muted-foreground">Simpler recipes (≤6 ingredients)</div>
              </div>
              <input
                type="checkbox"
                checked={fewerIngredients}
                onChange={(e) => setFewerIngredients(e.target.checked)}
                className="w-5 h-5 rounded accent-accent"
              />
            </label>
          </div>
          
          {/* Macro Targets Preview */}
          {weeklyTargets ? (
            <div className="bg-card rounded-xl border border-border/50 p-4 mb-6">
              <div className="text-xs text-muted-foreground mb-2">Your Weekly Targets</div>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div>
                  <div className="text-lg font-bold">{weeklyTargets.calories}</div>
                  <div className="text-xs text-muted-foreground">cal</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-red-500">{weeklyTargets.protein}g</div>
                  <div className="text-xs text-muted-foreground">protein</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-blue-500">{weeklyTargets.carbs}g</div>
                  <div className="text-xs text-muted-foreground">carbs</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-yellow-500">{weeklyTargets.fat}g</div>
                  <div className="text-xs text-muted-foreground">fat</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-6">
              <NutritionTargetsSetupPrompt />
            </div>
          )}
        </div>
      )}

      {/* Browse Mode - Weekly Macro Summary */}
      {planningMode === 'browse' && (
        <div className="px-4 py-3 bg-card border-b border-border/50">
          {weeklyTargets ? (
            <>
              <div className="text-xs text-muted-foreground mb-2">Weekly Totals</div>
              <div className="grid grid-cols-4 gap-2">
                <div className="text-center">
                  <div className="text-lg font-bold">{Math.round(weeklyMacros.calories)}</div>
                  <div className="text-xs text-muted-foreground">/ {weeklyTargets.calories} cal</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-red-500">{Math.round(weeklyMacros.protein)}g</div>
                  <div className="text-xs text-muted-foreground">/ {weeklyTargets.protein}g P</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-500">{Math.round(weeklyMacros.carbs)}g</div>
                  <div className="text-xs text-muted-foreground">/ {weeklyTargets.carbs}g C</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-yellow-500">{Math.round(weeklyMacros.fat)}g</div>
                  <div className="text-xs text-muted-foreground">/ {weeklyTargets.fat}g F</div>
                </div>
              </div>
            </>
          ) : (
            <NutritionTargetsSetupPrompt compact />
          )}
        </div>
      )}
      
      {/* Browse Mode - Category Tabs */}
      {planningMode === 'browse' && (
        <div className="flex border-b border-border/50 px-4 overflow-x-auto">
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex-shrink-0 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeCategory === cat.id 
                  ? 'border-accent text-foreground' 
                  : 'border-transparent text-muted-foreground'
              }`}
            >
            <span className="mr-1">{cat.emoji}</span>
            {cat.label}
            <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
              getCategoryCount(cat.id) > 0 ? 'bg-accent text-white' : 'bg-secondary'
            }`}>
              {getCategoryCount(cat.id)}/7
            </span>
          </button>
        ))}
      </div>
      )}
      
      {/* Browse Mode - Meal Grid */}
      {planningMode === 'browse' && (
      <div className="px-4 py-4">
        <div className="grid grid-cols-2 gap-3">
          {(mealsByCategory[activeCategory] || []).map(meal => {
            const isSelected = isMealSelected(meal.meal_id, activeCategory);
            
            return (
              <motion.div
                key={meal.meal_id}
                layout
                className={`relative bg-card rounded-xl border overflow-hidden cursor-pointer transition-all ${
                  isSelected ? 'border-accent ring-2 ring-accent/30' : 'border-border/50'
                }`}
                onClick={() => toggleMealSelection(meal, activeCategory)}
              >
                {/* Selection indicator */}
                {isSelected && (
                  <div className="absolute top-2 right-2 z-10 w-6 h-6 bg-accent rounded-full flex items-center justify-center">
                    <Check size={14} className="text-white" />
                  </div>
                )}
                
                {/* Meal image */}
                {meal.image_url ? (
                  <img 
                    src={meal.image_url} 
                    alt={meal.name}
                    className="w-full h-24 object-cover"
                  />
                ) : (
                  <div className="w-full h-24 bg-secondary/30 flex items-center justify-center">
                    <span className="text-2xl">🍽️</span>
                  </div>
                )}
                
                {/* Meal info */}
                <div className="p-2">
                  <h3 className="font-medium text-sm line-clamp-1">{meal.name || meal.title}</h3>
                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                    <span>{meal.calories || 0} cal</span>
                    <span>•</span>
                    <span>{meal.protein || 0}g P</span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
        
        {(mealsByCategory[activeCategory] || []).length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            No {activeCategory} meals available
          </div>
        )}
      </div>
      )}
      
      {/* Bottom Action Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background/80 backdrop-blur-lg border-t border-border/50">
        {planningMode === 'smart' ? (
          <Button
            onClick={handleSmartPlanGenerate}
            disabled={generating}
            className="w-full h-12 bg-accent hover:bg-accent/90"
            data-testid="smart-generate-btn"
          >
            {generating ? (
              <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2" />
            ) : (
              <Wand2 size={18} className="mr-2" />
            )}
            {generating ? 'Generating...' : 'Generate Smart Plan'}
          </Button>
        ) : (
          <>
            <Button
              onClick={handleGeneratePlan}
              disabled={getTotalSelectedCount() < 3 || generating}
              className="w-full h-12 bg-accent hover:bg-accent/90"
              data-testid="generate-plan-btn"
            >
              {generating ? (
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2" />
              ) : (
                <Sparkles size={18} className="mr-2" />
              )}
              Generate My Plan ({getTotalSelectedCount()} meals selected)
            </Button>
            
            {getTotalSelectedCount() < 3 && (
              <p className="text-xs text-center text-muted-foreground mt-2">
                Select at least 3 meals to generate a plan
              </p>
            )}
          </>
        )}
      </div>
      
      {/* Existing Plans Modal */}
      <AnimatePresence>
        {showExistingPlansModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-card rounded-2xl p-6 max-w-sm w-full"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <AlertCircle size={20} className="text-amber-500" />
                </div>
                <h2 className="font-semibold">Existing Plans Found</h2>
              </div>
              
              <p className="text-sm text-muted-foreground mb-4">
                You have {existingPlans.length} meals already planned for this week. 
                Would you like to build around them or start fresh?
              </p>
              
              <div className="space-y-2">
                <Button 
                  onClick={handleKeepExisting}
                  variant="outline"
                  className="w-full"
                >
                  Keep & Build Around
                </Button>
                <Button 
                  onClick={handleClearExisting}
                  variant="destructive"
                  className="w-full"
                >
                  Clear & Start Fresh
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Pin Meals Modal */}
      <AnimatePresence>
        {showPinModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-card rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold">Pin Meals to Dates?</h2>
                <button onClick={() => setShowPinModal(false)} className="p-1">
                  <X size={20} />
                </button>
              </div>
              
              <p className="text-sm text-muted-foreground mb-4">
                Do any meals need to be on specific days? (Optional - unpinned meals will be auto-assigned)
              </p>
              
              <div className="space-y-3 mb-6">
                {Object.entries(selectedMeals).flatMap(([category, meals]) =>
                  meals.map(meal => (
                    <div key={meal.meal_id} className="flex items-center justify-between p-2 bg-secondary/30 rounded-lg">
                      <span className="text-sm truncate flex-1">{meal.name || meal.title}</span>
                      <select
                        value={pinnedMeals[meal.meal_id] || ''}
                        onChange={e => {
                          if (e.target.value) {
                            setPinnedMeals(prev => ({ ...prev, [meal.meal_id]: e.target.value }));
                          } else {
                            setPinnedMeals(prev => {
                              const next = { ...prev };
                              delete next[meal.meal_id];
                              return next;
                            });
                          }
                        }}
                        className="ml-2 text-sm bg-background rounded px-2 py-1"
                      >
                        <option value="">Auto-assign</option>
                        {getWeekDates().map(date => (
                          <option key={date} value={date}>
                            {normalizeDate(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))
                )}
              </div>
              
              <div className="flex gap-2">
                <Button 
                  onClick={() => setShowPinModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={executeGeneration}
                  className="flex-1 bg-accent"
                  disabled={generating}
                >
                  {generating ? 'Generating...' : 'Generate Plan'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
